import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@koridor/api-client';

let wired = false;
let enabled = false;
let pendingToken: string | null = null;
/** Cold-start: action may fire before React router is ready. */
let pendingPushData: Record<string, unknown> | null = null;

function navigateFromPush(data: Record<string, unknown>) {
  const rematchFrom = typeof data.rematchFrom === 'string' ? data.rematchFrom : undefined;
  const matchId = typeof data.matchId === 'string' ? data.matchId : undefined;
  const inviteId = typeof data.inviteId === 'string' ? data.inviteId : undefined;

  if (rematchFrom) {
    window.location.assign(`/result?rematchFrom=${encodeURIComponent(rematchFrom)}`);
    return;
  }
  if (matchId) {
    window.location.assign(`/game?matchId=${encodeURIComponent(matchId)}`);
    return;
  }
  if (inviteId) {
    window.location.assign(`/invite/${encodeURIComponent(inviteId)}`);
  }
}

function handlePushAction(data: Record<string, unknown>) {
  // Defer until the SPA root exists so we don't lose cold-start taps.
  if (!document.getElementById('root')) {
    pendingPushData = data;
    return;
  }
  navigateFromPush(data);
}

/** Apply a push tap that arrived before the app finished booting. */
export function flushPendingPushNavigation() {
  if (!pendingPushData) return;
  const data = pendingPushData;
  pendingPushData = null;
  navigateFromPush(data);
}

async function uploadToken(token: string) {
  if (!enabled) {
    pendingToken = token;
    return;
  }
  if (!api.isAuthenticated) {
    pendingToken = token;
    return;
  }
  try {
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    await api.registerPushToken(platform, token);
    pendingToken = null;
  } catch (err) {
    pendingToken = token;
    console.warn('[push] failed to register token with API', err);
  }
}

async function wireListeners() {
  await PushNotifications.addListener('registration', (token) => {
    void uploadToken(token.value);
  });
  await PushNotifications.addListener('registrationError', (err) => {
    console.warn('[push] registration error', err);
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    const data = (event.notification.data ?? {}) as Record<string, unknown>;
    handlePushAction(data);
  });
}

/** Wire push tap listeners as early as possible (before auth / AppShell). */
export async function initNativePushListenersEarly() {
  if (!Capacitor.isNativePlatform()) return;
  if (wired) {
    flushPendingPushNavigation();
    return;
  }
  wired = true;
  try {
    await wireListeners();
  } catch (err) {
    wired = false;
    console.warn('[push] failed to wire early listeners', err);
  }
  flushPendingPushNavigation();
}

export async function ensureNativeNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const perm = await PushNotifications.requestPermissions();
  return perm.receive === 'granted';
}

export async function registerNativePush(notificationsEnabled: boolean) {
  if (!Capacitor.isNativePlatform()) return;

  enabled = notificationsEnabled;
  if (!notificationsEnabled) return;

  if (!wired) {
    wired = true;
    await wireListeners();
  }

  flushPendingPushNavigation();

  if (pendingToken) {
    void uploadToken(pendingToken);
  }

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;
  await PushNotifications.register();
}
